import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { supabase } from '../../services/supabaseClient/supabaseClient';
import ActivityCard from './ActivityCard';
import AddActivityModal from './AddActivityModal';
import ReflectionModal from './ReflectionModal';
import { useAgentModal } from '../../context/AgentModalContext';
import { selectAllTimers } from '../../redux/taskTimerSlice';
import {
  fetchUserActivities,
  createActivity,
  deleteActivity,
  toggleActivityCompletion,
} from '../../services/supabaseClient/activitiesService';
import {
  setActivities,
  addActivity,
  removeActivity,
  toggleActivityComplete,
  setLoading,
  setError as setErrorAction,
  selectAllActivities,
  selectActivitiesLoading,
  selectActivitiesError,
  selectLastFetched,
} from '../../redux/activitiesSlice';
import { useActivitySync } from '../../hooks/useActivitySync';
import LoadingSpinner from '../ui/LoadingSpinner';
import './TasksSidebar.css';

const TasksSidebar = ({ open = false, onClose }) => {
  const dispatch = useDispatch();
  const { openAgentModal } = useAgentModal();
  const activities = useSelector(selectAllActivities);
  const loading = useSelector(selectActivitiesLoading);
  const error = useSelector(selectActivitiesError);
  const lastFetched = useSelector(selectLastFetched);
  const allTimers = useSelector(selectAllTimers);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [activityToComplete, setActivityToComplete] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [userId, setUserId] = useState(null);
  const [sortBy, setSortBy] = useState('date'); // 'date', 'priority', 'status', 'created'
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'date', 'priority', 'status'
  const [filterPriority, setFilterPriority] = useState('all'); // 'all', 'low', 'medium', 'high', 'urgent'
  const [showFilters, setShowFilters] = useState(false);

  // Get current user ID for real-time sync
  useEffect(() => {
    const getUserId = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
    };
    getUserId();
  }, []);

  // Enable real-time synchronization across devices
  useActivitySync(userId, true);

  // Fetch activities from database on component mount (with smart caching)
  useEffect(() => {
    // Only fetch if data is stale (older than 30 seconds) or doesn't exist
    const CACHE_DURATION = 30000; // 30 seconds
    const now = Date.now();
    const isStale = !lastFetched || now - lastFetched > CACHE_DURATION;

    if (isStale || activities.length === 0) {
      loadActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadActivities = async () => {
    dispatch(setLoading(true));
    dispatch(setErrorAction(null));
    const { data, error: fetchError } = await fetchUserActivities();

    if (fetchError) {
      // eslint-disable-next-line no-console
      console.error('Error loading activities:', fetchError);
      dispatch(setErrorAction('Failed to load activities. Please try again.'));
      return;
    }

    // Map database fields to component format
    const mappedActivities = (data || []).map((task) => ({
      id: task.id,
      user_id: task.user_id,
      who: task.task_owner,
      what: task.task_title,
      description: task.task_description || '',
      task_description: task.task_description,
      when: task.task_deadline,
      urgencyLevel: task.task_urgency_level,
      priority: task.priority || 'medium',
      status: task.status || 'todo',
      completed: task.completed,
      aiGenerated: task.ai_generated,
      learningGoal: task.learning_goal,
      reflection: task.task_reflection,
      reflection_notes: task.task_reflection,
      ai_reflection: task.ai_reflection,
      scratch_pad: task.scratch_pad,
      conversation_summary: task.task_conversation_summary,
      createdAt: task.created_at,
      startedAt: task.started_at,
      finishedAt: task.finished_at,
      completedAt: task.completed_at,
      estimated_minutes: task.estimated_minutes,
      time_remaining_seconds: task.time_remaining_seconds,
      time_spent: task.time_spent || 0,
      timer_paused: task.timer_paused || false,
      projectId: task.associated_project_id || null,
      courseId: task.associated_course_id || null,
      // Include all reflection metadata
      reflection_insights: task.reflection_insights,
      reflection_patterns: task.reflection_patterns,
      reflection_recommendations: task.reflection_recommendations,
      reflection_generated_at: task.reflection_generated_at,
      key_learnings: task.key_learnings,
      // Rich card fields
      is_pinned: task.is_pinned || false,
      progress: task.progress || 0,
      tags: task.tags || [],
      card_color: task.card_color,
      icon: task.icon,
    }));

    dispatch(setActivities(mappedActivities));
  };

  const handleAddActivity = async (newActivity) => {
    const { data, error: createError } = await createActivity(newActivity);

    if (createError) {
      // eslint-disable-next-line no-console
      console.error('Error creating activity:', createError);
      dispatch(setErrorAction('Failed to create activity. Please try again.'));
      return;
    }

    // Map database fields to component format (same as loadActivities)
    const mappedActivity = {
      id: data.id,
      user_id: data.user_id,
      who: data.task_owner,
      what: data.task_title,
      description: data.task_description || '',
      task_description: data.task_description,
      when: data.task_deadline,
      urgencyLevel: data.task_urgency_level,
      priority: data.priority || 'medium',
      status: data.status || 'todo',
      completed: data.completed,
      aiGenerated: data.ai_generated,
      learningGoal: data.learning_goal,
      reflection: data.task_reflection,
      reflection_notes: data.task_reflection,
      ai_reflection: data.ai_reflection,
      scratch_pad: data.scratch_pad,
      conversation_summary: data.task_conversation_summary,
      createdAt: data.created_at,
      startedAt: data.started_at,
      finishedAt: data.finished_at,
      completedAt: data.completed_at,
      estimated_minutes: data.estimated_minutes,
      time_remaining_seconds: data.time_remaining_seconds,
      time_spent: data.time_spent || 0,
      timer_paused: data.timer_paused || false,
      projectId: data.associated_project_id || null,
      courseId: data.associated_course_id || null,
      // Include all reflection metadata
      reflection_insights: data.reflection_insights,
      reflection_patterns: data.reflection_patterns,
      reflection_recommendations: data.reflection_recommendations,
      reflection_generated_at: data.reflection_generated_at,
      key_learnings: data.key_learnings,
      // Rich card fields
      is_pinned: data.is_pinned || false,
      progress: data.progress || 0,
      tags: data.tags || [],
      card_color: data.card_color,
      icon: data.icon,
    };

    dispatch(addActivity(mappedActivity));
    setShowAddModal(false);
  };

  const handleToggleComplete = async (id) => {
    const activity = activities.find((a) => a.id === id);

    // If marking as complete, show reflection modal
    if (activity && !activity.completed) {
      setActivityToComplete(activity);
      setShowReflectionModal(true);
    } else {
      // If marking as incomplete, just toggle in database
      const { error: toggleError } = await toggleActivityCompletion(id);

      if (toggleError) {
        // eslint-disable-next-line no-console
        console.error('Error toggling activity:', toggleError);
        dispatch(setErrorAction('Failed to update activity. Please try again.'));
        return;
      }

      // Update Redux state
      dispatch(toggleActivityComplete(id));
    }
  };

  const handleReflectionSubmit = async (reflection) => {
    if (activityToComplete) {
      const { error: updateError } = await toggleActivityCompletion(
        activityToComplete.id,
        reflection
      );

      if (updateError) {
        // eslint-disable-next-line no-console
        console.error('Error completing activity:', updateError);
        dispatch(setErrorAction('Failed to complete activity. Please try again.'));
        setShowReflectionModal(false);
        setActivityToComplete(null);
        return;
      }

      // Update Redux state
      dispatch(toggleActivityComplete(activityToComplete.id));
    }
    setShowReflectionModal(false);
    setActivityToComplete(null);
  };

  const handleDeleteActivity = async (id) => {
    const { error: deleteError } = await deleteActivity(id);

    if (deleteError) {
      // eslint-disable-next-line no-console
      console.error('Error deleting activity:', deleteError);
      dispatch(setErrorAction('Failed to delete activity. Please try again.'));
      return;
    }

    dispatch(removeActivity(id));
  };

  // Helper functions for date grouping
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    const taskDate = new Date(date);
    return (
      taskDate.getDate() === today.getDate() &&
      taskDate.getMonth() === today.getMonth() &&
      taskDate.getFullYear() === today.getFullYear()
    );
  };

  const isThisWeek = (date) => {
    if (!date) return false;
    const today = new Date();
    const taskDate = new Date(date);
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);
    return taskDate >= weekStart && taskDate < weekEnd;
  };

  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  // Filter activities
  let filteredActivities = activities.filter((a) => {
    if (showDone && !a.completed) return false;
    if (!showDone && a.completed) return false;
    if (filterPriority !== 'all' && a.priority !== filterPriority) return false;
    return true;
  });

  // Sort activities
  const sortActivities = (acts) => {
    const sorted = [...acts];
    switch (sortBy) {
      case 'date':
        sorted.sort((a, b) => {
          const dateA = new Date(a.when || a.createdAt);
          const dateB = new Date(b.when || b.createdAt);
          return dateA - dateB;
        });
        break;
      case 'priority': {
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
        sorted.sort((a, b) => {
          const priorityA = priorityOrder[a.priority] ?? 4;
          const priorityB = priorityOrder[b.priority] ?? 4;
          return priorityA - priorityB;
        });
        break;
      }
      case 'status': {
        const statusOrder = { todo: 0, in_progress: 1, done: 2 };
        sorted.sort((a, b) => {
          const statusA = statusOrder[a.status] ?? 3;
          const statusB = statusOrder[b.status] ?? 3;
          return statusA - statusB;
        });
        break;
      }
      case 'created':
        sorted.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA; // Newest first
        });
        break;
      default:
        break;
    }
    return sorted;
  };

  // Group activities
  const groupActivities = (acts) => {
    if (groupBy === 'none') {
      return { 'All Tasks': acts };
    }

    const groups = {};
    acts.forEach((activity) => {
      let groupKey;
      switch (groupBy) {
        case 'date':
          if (isOverdue(activity.when) && !activity.completed) {
            groupKey = 'Overdue';
          } else if (isToday(activity.when)) {
            groupKey = 'Today';
          } else if (isThisWeek(activity.when)) {
            groupKey = 'This Week';
          } else {
            groupKey = 'Later';
          }
          break;
        case 'priority':
          groupKey =
            (activity.priority || 'medium').charAt(0).toUpperCase() +
            (activity.priority || 'medium').slice(1);
          break;
        case 'status':
          groupKey = (activity.status || 'todo')
            .replace('_', ' ')
            .split(' ')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
          break;
        default:
          groupKey = 'All Tasks';
      }
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(activity);
    });

    // Sort group keys for consistent display
    if (groupBy === 'date') {
      const orderedKeys = ['Overdue', 'Today', 'This Week', 'Later'];
      const orderedGroups = {};
      orderedKeys.forEach((key) => {
        if (groups[key]) {
          orderedGroups[key] = groups[key];
        }
      });
      return orderedGroups;
    } else if (groupBy === 'priority') {
      const orderedKeys = ['Urgent', 'High', 'Medium', 'Low'];
      const orderedGroups = {};
      orderedKeys.forEach((key) => {
        if (groups[key]) {
          orderedGroups[key] = groups[key];
        }
      });
      return orderedGroups;
    }

    return groups;
  };

  const sortedActivities = sortActivities(filteredActivities);
  const groupedActivities = groupActivities(sortedActivities);

  const doneActivities = activities.filter((a) => a.completed);

  return (
    <>
      {/* Overlay */}
      <div
        className="tasks-sidebar-overlay"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close tasks sidebar"
        aria-hidden={!open}
        style={{ display: open ? 'block' : 'none' }}
      />

      {/* Sidebar */}
      <aside
        className={`tasks-sidebar ${open ? 'open' : ''}`}
        aria-label="Tasks Sidebar"
        aria-hidden={!open}
      >
        <div className="tasks-sidebar-header">
          <div className="tasks-sidebar-title">
            <i className="fa-solid fa-list-check" aria-hidden="true" />
            <h2>My Tasks</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tasks-sidebar-close"
            aria-label="Close tasks sidebar"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        {/* Sticky Controls Section */}
        <div className="tasks-sidebar-controls">
          {/* Error Message */}
          {error && (
            <div
              className="error-message"
              style={{ color: 'red', padding: '10px', marginBottom: '10px' }}
            >
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons-container">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`filter-btn icon-only ${showFilters ? 'active' : ''}`}
              aria-label="Toggle filters"
              title="Filters & Sort"
            >
              <i className="fa-solid fa-filter" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setShowDone(!showDone)}
              className={`view-done-btn icon-only ${showDone ? 'active' : ''}`}
              aria-label={showDone ? 'Hide done activities' : 'View done activities'}
              title={showDone ? 'Hide done' : 'View done'}
            >
              <i className="fa-solid fa-check-circle" aria-hidden="true" />
              {doneActivities.length > 0 && (
                <span className="done-badge">{doneActivities.length}</span>
              )}
            </button>
            <button
              type="button"
              onClick={async () => {
                // Check if there's a running task
                const runningTaskId = Object.keys(allTimers).find(
                  (id) => allTimers[id]?.timerActive && !allTimers[id]?.timerPaused
                );
                let contextPayload = {};
                if (runningTaskId) {
                  const runningTask = activities.find((a) => a.id === runningTaskId);
                  if (runningTask) {
                    contextPayload = {
                      taskId: runningTask.id,
                      taskTitle: runningTask.what || runningTask.activity_what,
                      isRunning: true,
                    };
                  }
                }
                openAgentModal({
                  agentId: 'tasks',
                  origin: 'tasks-sidebar',
                  contextPayload,
                });
              }}
              className="ai-assistant-btn icon-only"
              aria-label="AI Task Assistant"
              title="AI Task Assistant"
            >
              <i className="fa-solid fa-brain" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="add-activity-btn icon-only"
              aria-label="Add task"
              title="Add task"
            >
              <i className="fa-solid fa-plus" aria-hidden="true" />
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label htmlFor="sort-by">
                  <i className="fa-solid fa-sort" aria-hidden="true" />
                  Sort by:
                </label>
                <select
                  id="sort-by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="filter-select"
                >
                  <option value="date">Due Date</option>
                  <option value="priority">Priority</option>
                  <option value="status">Status</option>
                  <option value="created">Created Date</option>
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="group-by">
                  <i className="fa-solid fa-layer-group" aria-hidden="true" />
                  Group by:
                </label>
                <select
                  id="group-by"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="filter-select"
                >
                  <option value="none">None</option>
                  <option value="date">Date</option>
                  <option value="priority">Priority</option>
                  <option value="status">Status</option>
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="filter-priority">
                  <i className="fa-solid fa-flag" aria-hidden="true" />
                  Priority:
                </label>
                <select
                  id="filter-priority"
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable Content Section */}
        <div className="tasks-sidebar-content">
          {/* Loading State */}
          {loading && (
            <LoadingSpinner message="Loading tasks..." size="sm" className="loading-state" />
          )}

          {/* Organized Activities */}
          {Object.keys(groupedActivities).length > 0 && (
            <>
              {Object.entries(groupedActivities).map(([groupName, groupActivities]) => (
                <div key={groupName} className="activities-section">
                  <h3 className="section-title">
                    {groupBy === 'date' && groupName === 'Overdue' && (
                      <i
                        className="fa-solid fa-exclamation-triangle"
                        aria-hidden="true"
                        style={{ color: '#ff4444' }}
                      />
                    )}
                    {groupBy === 'date' && groupName === 'Today' && (
                      <i className="fa-solid fa-calendar-day" aria-hidden="true" />
                    )}
                    {groupBy === 'date' && groupName === 'This Week' && (
                      <i className="fa-solid fa-calendar-week" aria-hidden="true" />
                    )}
                    {groupBy === 'date' && groupName === 'Later' && (
                      <i className="fa-regular fa-calendar" aria-hidden="true" />
                    )}
                    {groupBy === 'priority' && (
                      <i className="fa-solid fa-flag" aria-hidden="true" />
                    )}
                    {groupBy === 'status' && <i className="fa-solid fa-tasks" aria-hidden="true" />}
                    {groupBy === 'none' && (
                      <i
                        className={`fa-solid ${showDone ? 'fa-check-circle' : 'fa-clock'}`}
                        aria-hidden="true"
                      />
                    )}
                    {groupName} ({groupActivities.length})
                  </h3>
                  <div className="activities-list">
                    {groupActivities.map((activity) => (
                      <ActivityCard
                        key={activity.id}
                        activity={activity}
                        onToggleComplete={handleToggleComplete}
                        onDelete={handleDeleteActivity}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Empty State */}
          {!loading && Object.keys(groupedActivities).length === 0 && (
            <div className="empty-state">
              {showDone ? (
                <>
                  <i className="fa-solid fa-check-circle" aria-hidden="true" />
                  <p>No completed tasks</p>
                  <p className="empty-state-hint">Complete some tasks to see them here!</p>
                </>
              ) : activities.length === 0 ? (
                <>
                  <i className="fa-solid fa-inbox" aria-hidden="true" />
                  <p>No tasks yet</p>
                  <p className="empty-state-hint">Add your first task to get started!</p>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-filter" aria-hidden="true" />
                  <p>No tasks match your filters</p>
                  <p className="empty-state-hint">Try adjusting your filters</p>
                </>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Add Activity Modal */}
      {showAddModal && (
        <AddActivityModal onClose={() => setShowAddModal(false)} onAdd={handleAddActivity} />
      )}

      {/* Reflection Modal */}
      {showReflectionModal && activityToComplete && (
        <ReflectionModal
          activity={activityToComplete}
          onClose={() => {
            setShowReflectionModal(false);
            setActivityToComplete(null);
          }}
          onSubmit={handleReflectionSubmit}
        />
      )}
    </>
  );
};

export default TasksSidebar;
